// native
import {
  AfterContentInit, ChangeDetectorRef, Component, ContentChildren,
  EventEmitter, Injector, Input, OnDestroy, Output, QueryList
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MIN_RECORDS_FOR_PAGINATION } from '../../../constants';

// models
import {
  GridItem, CompositeFilterDescriptor, SortDescriptor, SortDirection, PageChangeEvent
} from 'app/models';

// component / service
import { DataGridColumnComponent } from './data-grid-column.component';
import { GridFilterService } from './grid-filter.service';

/**
 * Custom replacement for <kendo-grid>. Server-side paging/sorting/filtering:
 * the grid renders the current page ({ data, total }) and emits change events;
 * the host re-queries. The column API mirrors kendo's so views migrate 1:1.
 */
@Component({
    selector: 'app-data-grid',
    templateUrl: './data-grid.component.html',
    providers: [GridFilterService],
    standalone: false
})
export class DataGridComponent implements AfterContentInit, OnDestroy {
  @Input() data: GridItem<any> | null;
  @Input() loading = false;
  @Input() filterable = false;
  @Input() filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
  @Input() sortable = false;
  @Input() sort: SortDescriptor[] = [];
  @Input() pageable = false;
  @Input() pageSize = 50;
  @Input() skip = 0;
  @Input() height: number;

  @Output() filterChange = new EventEmitter<CompositeFilterDescriptor>();
  @Output() sortChange = new EventEmitter<SortDescriptor[]>();
  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  @ContentChildren(DataGridColumnComponent) columns: QueryList<DataGridColumnComponent>;

  /** Snapshot of the projected columns (handles role-gated *ngIf columns). */
  colList: DataGridColumnComponent[] = [];

  /** Injector that exposes this grid's GridFilterService to projected filter cells. */
  readonly filterInjector: Injector;

  private sub = new Subscription();

  constructor(
    public filterService: GridFilterService,
    private cdr: ChangeDetectorRef,
    injector: Injector
  ) {
    this.filterInjector = Injector.create({
      providers: [{ provide: GridFilterService, useValue: this.filterService }],
      parent: injector
    });
  }

  ngAfterContentInit(): void {
    this.colList = this.columns.toArray();
    this.sub.add(this.columns.changes.subscribe(() => {
      this.colList = this.columns.toArray();
      this.cdr.markForCheck();
    }));
    this.sub.add(this.filterService.changes.subscribe(filter => this.filterChange.emit(filter)));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get rows(): any[] {
    return this.data?.data ?? [];
  }

  get total(): number {
    return this.data?.total ?? 0;
  }

  get showPager(): boolean {
    return this.pageable && this.total > MIN_RECORDS_FOR_PAGINATION;
  }

  isSortable(col: DataGridColumnComponent): boolean {
    return this.sortable && col.sortable !== false && !col.isCommand;
  }

  isFilterable(col: DataGridColumnComponent): boolean {
    return this.filterable && col.filterable !== false && !col.isCommand;
  }

  sortDir(col: DataGridColumnComponent): SortDirection | undefined {
    const current = this.sort?.[0];
    return current && current.field === col.field ? current.dir : undefined;
  }

  ariaSort(col: DataGridColumnComponent): 'ascending' | 'descending' | 'none' {
    const dir = this.sortDir(col);
    return dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none';
  }

  onSort(col: DataGridColumnComponent): void {
    if (!this.isSortable(col)) {
      return;
    }
    const current = this.sort?.[0];
    let dir: SortDirection | undefined;
    if (!current || current.field !== col.field) {
      dir = 'asc';
    } else if (current.dir === 'asc') {
      dir = 'desc';
    } else if (current.dir === 'desc') {
      dir = undefined; // allowUnsort (third click clears the sort)
    } else {
      dir = 'asc';
    }
    this.sortChange.emit([{ field: col.field, dir }]);
  }

  onPageChange(event: PageChangeEvent): void {
    this.pageChange.emit(event);
  }

  filterContext(col: DataGridColumnComponent): { $implicit: CompositeFilterDescriptor; column: DataGridColumnComponent; } {
    return { $implicit: this.filter, column: col };
  }

  cellContext(row: any): { $implicit: any; dataItem: any; } {
    return { $implicit: row, dataItem: row };
  }
}
